import React, { useRef, useState, useCallback } from 'react';
import { Tooltip, TooltipProps } from '@mui/material';

interface OverflowTooltipProps extends Omit<TooltipProps, 'children'> {
  children: React.ReactElement;
}

/**
 * A Tooltip that only appears when its child element's text is truncated
 * (i.e. scrollWidth > clientWidth). Otherwise renders the child with no tooltip.
 */
const OverflowTooltip: React.FC<OverflowTooltipProps> = ({ children, ...tooltipProps }) => {
  const [isOverflowed, setIsOverflowed] = useState(false);
  const textRef = useRef<HTMLElement | null>(null);
  const child = React.Children.only(children);

  const handleMouseEnter = useCallback(() => {
    const el = textRef.current;
    if (el) {
      setIsOverflowed(el.scrollWidth > el.clientWidth);
    }
  }, []);

  const childProps = {
    ref: (node: HTMLElement | null) => {
      textRef.current = node;

      const originalRef = (child.props as { ref?: React.Ref<HTMLElement> }).ref;
      if (typeof originalRef === 'function') {
        originalRef(node);
      } else if (originalRef && typeof originalRef === 'object') {
        originalRef.current = node;
      }
    },
    onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
      (child.props as Record<string, any>).onMouseEnter?.(event);
      handleMouseEnter();
    },
  };

  return (
    <Tooltip placement="top" {...tooltipProps} disableHoverListener={!isOverflowed}>
      {React.cloneElement(child, childProps)}
    </Tooltip>
  );
};

export default OverflowTooltip;
